import type { v4, Manifest } from '@sap/ux-specification-types';
import { descriptionSrcURL, syncRule, validity } from '../../../common/decoration/decorators';
import {
    getGroupedActionsPathForLR,
    getActionsPathForLR,
    getActionsPathForView,
    getActionPositionPathForView
} from '../../application';
import { addPatternForBindingChangeOfEnumLR } from '../../../common/decoration/control';
import { exportActionMenuAction, exportActionMenuDefaultAction, exportActionMenuMenu } from '../actions';

export class ActionAfterExecutionConfigurationToolBarLR implements v4.ActionAfterExecutionConfiguration {
    @syncRule({
        manifest: {
            path: (
                pageName: string[],
                manifest: Manifest,
                sectionId: string,
                columnKey: string,
                targetAnnotation: string
            ): string =>
                getActionsPathForLR(pageName, manifest, sectionId, columnKey, targetAnnotation) + '/afterExecution'
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/2c65f07f44094012a511d6bd83f50f2d')
    navigateToInstance?: boolean;
    @syncRule({
        manifest: {
            path: (
                pageName: string[],
                manifest: Manifest,
                sectionId: string,
                columnKey: string,
                targetAnnotation: string
            ): string =>
                getActionsPathForLR(pageName, manifest, sectionId, columnKey, targetAnnotation) + '/afterExecution'
        }
    })
    enableAutoScroll?: boolean;
}

export class ToolBarActionBase implements v4.ToolBarActionBase {
    @syncRule({
        manifest: {
            path: getActionsPathForLR
        }
    })
    enableOnSelect?: v4.SelectType;

    @syncRule({
        manifest: {
            path: getActionsPathForLR
        }
    })
    afterExecution?: ActionAfterExecutionConfigurationToolBarLR;
}

export class ToolBarAction extends ToolBarActionBase implements v4.ToolBarAction {
    @syncRule({
        manifest: {
            path: getActionsPathForLR
        }
    })
    @validity({
        since: '1.142.0'
    })
    overflowGroup?: number;

    @syncRule({
        manifest: {
            path: getActionsPathForLR
        }
    })
    @validity({
        since: '1.139.0'
    })
    priority?: v4.ActionPriority;
}

export class ToolBarActionGroup implements v4.ToolBarActionGroup {
    [key: string]: ToolBarActionBase | number | v4.ActionPriority;

    @syncRule({
        manifest: {
            path: getGroupedActionsPathForLR
        }
    })
    @validity({
        since: '1.142.0'
    })
    overflowGroup?: number;

    @syncRule({
        manifest: {
            path: getGroupedActionsPathForLR
        }
    })
    @validity({
        since: '1.139.0'
    })
    priority?: v4.ActionPriority;
}

export class ViewToolBarActionGroup implements v4.ViewToolBarActionGroup {
    [key: string]: ViewToolBarActionBase | number | v4.ActionPriority;

    @syncRule({
        manifest: {
            path: getGroupedActionsPathForLR
        }
    })
    @validity({
        since: '1.142.0'
    })
    overflowGroup?: number;

    @syncRule({
        manifest: {
            path: getGroupedActionsPathForLR
        }
    })
    @validity({
        since: '1.139.0'
    })
    priority?: v4.ActionPriority;
}

export class ViewToolBarActionBase implements v4.ViewToolBarActionBase {
    @syncRule({
        manifest: {
            path: getActionsPathForView
        }
    })
    enableOnSelect?: v4.SelectType;

    @syncRule({
        manifest: {
            path: getActionsPathForView
        }
    })
    afterExecution?: ActionAfterExecutionConfigurationToolBarLR;
}

export class ViewToolBarAction extends ViewToolBarActionBase implements v4.ViewToolBarAction {
    @syncRule({
        manifest: {
            path: getActionsPathForView
        }
    })
    @validity({
        since: '1.142.0'
    })
    overflowGroup?: number;

    @syncRule({
        manifest: {
            path: getActionsPathForView
        }
    })
    @validity({
        since: '1.139.0'
    })
    priority?: v4.ActionPriority;
}

/**
 * Sync class for Position
 */
export class CustomActionPosition implements v4.CustomActionPosition {
    @syncRule({
        manifest: {
            path: (
                pageName: string[],
                manifest: Manifest,
                sectionId: string,
                columnKey: string,
                targetAnnotation: string
            ): string => getActionsPathForLR(pageName, manifest, sectionId, columnKey, targetAnnotation) + '/position'
        }
    })
    @validity({
        since: '1.84.0'
    })
    anchor?: string;
    @syncRule({
        manifest: {
            path: (
                pageName: string[],
                manifest: Manifest,
                sectionId: string,
                columnKey: string,
                targetAnnotation: string
            ): string => getActionsPathForLR(pageName, manifest, sectionId, columnKey, targetAnnotation) + '/position'
        }
    })
    @validity({
        since: '1.84.0'
    })
    placement: v4.ActionPlacement;
}

export class CustomTableActionBase implements v4.CustomActionElement {
    @syncRule({
        manifest: {
            path: getActionsPathForLR,
            export: exportActionMenuMenu
        }
    })
    @validity({
        since: '1.84.0'
    })
    menu: string[];

    @syncRule({
        manifest: {
            path: getActionsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    text: string;

    @syncRule({
        manifest: {
            path: getActionsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    position: CustomActionPosition;

    @syncRule({
        manifest: {
            path: getActionsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    requiresSelection?: boolean;

    @syncRule({
        manifest: {
            path: getActionsPathForLR
        },
        generate: addPatternForBindingChangeOfEnumLR
    })
    @validity({
        since: '1.84.0'
    })
    visible?: boolean;

    @syncRule({
        manifest: {
            path: getActionsPathForLR
        },
        generate: addPatternForBindingChangeOfEnumLR
    })
    @validity({
        since: '1.84.0'
    })
    enabled?: boolean;

    @syncRule({
        manifest: {
            path: getActionsPathForLR
        }
    })
    @validity({
        since: '1.142.0'
    })
    overflowGroup?: number;

    @syncRule({
        manifest: {
            path: getActionsPathForLR
        }
    })
    @validity({
        since: '1.139.0'
    })
    priority?: v4.ActionPriority;
}

export class CustomTableAction extends CustomTableActionBase implements v4.CustomTableAction {
    @syncRule({
        manifest: {
            path: getActionsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    press: string;
}

/**
 * Sync class for Position
 */
export class ViewCustomActionPosition implements v4.ViewCustomActionPosition {
    @syncRule({
        manifest: {
            path: getActionPositionPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    anchor?: string;
    @syncRule({
        manifest: {
            path: getActionPositionPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    placement: v4.ActionPlacement;
}

export class ViewTableCustomActionBase implements v4.CustomActionElement {
    @syncRule({
        manifest: {
            path: getActionsPathForView,
            export: exportActionMenuMenu
        }
    })
    @validity({
        since: '1.84.0'
    })
    menu: string[];

    @syncRule({
        manifest: {
            path: getActionsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    text: string;

    @syncRule({
        manifest: {
            path: getActionsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    position: CustomActionPosition;

    @syncRule({
        manifest: {
            path: getActionsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    requiresSelection?: boolean;

    @syncRule({
        manifest: {
            path: getActionsPathForView
        },
        generate: addPatternForBindingChangeOfEnumLR
    })
    @validity({
        since: '1.84.0'
    })
    visible?: boolean;

    @syncRule({
        manifest: {
            path: getActionsPathForView
        },
        generate: addPatternForBindingChangeOfEnumLR
    })
    @validity({
        since: '1.84.0'
    })
    enabled?: boolean;
    @syncRule({
        manifest: {
            path: getActionsPathForView
        }
    })
    @validity({
        since: '1.142.0'
    })
    overflowGroup?: number;

    @syncRule({
        manifest: {
            path: getActionsPathForView
        }
    })
    @validity({
        since: '1.139.0'
    })
    priority?: v4.ActionPriority;
}

export class ViewTableCustomAction extends ViewTableCustomActionBase implements v4.ViewTableCustomAction {
    @syncRule({
        manifest: {
            path: getActionsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    press: string;
}

export class ViewCustomActionMenu extends ViewTableCustomActionBase implements v4.ViewCustomActionMenu {
    @syncRule({
        manifest: {
            path: getActionsPathForView,
            export: exportActionMenuAction
        }
    })
    @validity({
        since: '1.84.0'
    })
    actions?: ViewCustomActionMenuActions;

    @syncRule({
        manifest: {
            path: getActionsPathForView,
            export: exportActionMenuDefaultAction
        }
    })
    @validity({
        since: '1.84.0'
    })
    defaultAction?: string;
}

export class ViewCustomActionMenuActions implements v4.ViewCustomActionMenuActions {
    [id: string]: ToolBarAction | CustomTableAction | ToolBarActionGroup;
}
